import React from 'react';

import css from './SpecFieldIcon.module.css';
import feedingIcon from './images/feedingIcon.png';
import flowIcon from './images/flowIcon.png';
import lightingIcon from './images/lightingIcon.png';
import reefSafeIcon from './images/reefSafeIcon.png';
import sizeIcon from './images/sizeIcon.png';
import tankIcon from './images/tankIcon.png';
import temperamentIcon from './images/temperamentIcon.png';
import weightIcon from './images/weightIcon.png';

/**
 * Stroke-only path sets for Console listing field keys.
 * Prefer open outlines with interior detail so icons don't read as solid blobs at 15px.
 */
const pathsByKey = {
  wysiwyg_coral: [
    'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z',
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  ],
  Origin_listing: [
    'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z',
    'M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  ],
  qt_stat: ['M12 3 5 6v5.5c0 4.5 3 7.8 7 10 4-2.2 7-5.5 7-10V6l-7-3Z', 'M9.5 12.2 11.2 14l3.5-3.8'],
  // Badge / seal outline (not a review star).
  condition_dry: [
    'M12 2.8 14.6 4.2l3 .7.7 3 1.4 2.6-1.4 2.6-.7 3-3 .7L12 21.2l-2.6-1.4-3-.7-.7-3L4.3 13.5l1.4-2.6.7-3 3-.7L12 2.8Z',
    'M9.4 12.1 11.1 13.8l3.6-3.8',
  ],
  brand_number: ['M5 7h14', 'M5 12h14', 'M5 17h10'],
  price_structure: [
    'M12 3v18',
    'M16.5 7.2c0-1.7-1.8-3-4.5-3S7.5 5.5 7.5 7.2 9.3 10 12 10s4.5 1.3 4.5 3-1.8 3-4.5 3-4.5-1.3-4.5-3',
  ],
  gallons_total: ['M12 3.5c-3.2 4.2-5.5 7.2-5.5 9.8a5.5 5.5 0 0 0 11 0c0-2.6-2.3-5.6-5.5-9.8Z'],
  water_volume_sumps: [
    'M12 3.5c-3.2 4.2-5.5 7.2-5.5 9.8a5.5 5.5 0 0 0 11 0c0-2.6-2.3-5.6-5.5-9.8Z',
  ],
  rack_plug_id: [
    'M4.5 4.5h6v6h-6z',
    'M13.5 4.5h6v6h-6z',
    'M4.5 13.5h6v6h-6z',
    'M13.5 13.5h6v6h-6z',
  ],
};

/** Raster silhouettes tinted via CSS mask to match muted icon color. */
const imageByKey = {
  reef_safe: reefSafeIcon,
  tank_types: tankIcon,
  tank_size: tankIcon,
  size_coral: sizeIcon,
  flow_req: flowIcon,
  lighting_requirement_coral: lightingIcon,
  weight_pounds: weightIcon,
  weight_volume: weightIcon,
  temp_fish: temperamentIcon,
  feeding_requirments: feedingIcon,
};

const fallbackPaths = ['M12 21.5a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19Z', 'M12 11v5', 'M12 8h.01'];

/**
 * Leading muted icon for a listing Details row.
 *
 * @param {Object} props
 * @param {string} props.fieldKey - Console listing field key
 */
const SpecFieldIcon = props => {
  const { fieldKey } = props;
  const imageSrc = imageByKey[fieldKey];

  if (imageSrc) {
    return (
      <span
        className={css.imageIcon}
        style={{
          WebkitMaskImage: `url(${imageSrc})`,
          maskImage: `url(${imageSrc})`,
        }}
        aria-hidden="true"
      />
    );
  }

  const paths = pathsByKey[fieldKey] || fallbackPaths;

  return (
    <svg
      className={css.root}
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths.map((d, index) => (
        <path key={`${fieldKey || 'fallback'}-${index}`} d={d} fill="none" />
      ))}
    </svg>
  );
};

export default SpecFieldIcon;
